import { LitElement, html } from 'lit';
import { customElement, state, query, property } from 'lit/decorators.js';
import type { SlDialog } from '@shoelace-style/shoelace';

// Shoelace component imports
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

// Import the image cropper component
import 'lit-image-cropper';
import { styles } from './image-picker-styles.js';
import { terms } from '../../language/terms';

// Define the possible states for the image picking process - simplified to just two states
enum ImagePickerState {
  Selection,
  Cropping,
}

@customElement('image-picker-modal')
export class ImagePickerModal extends LitElement {
  static styles = [styles];

  @property({ type: String })
  dialogTitle = 'Select Image';

  @property({ type: Number })
  aspectRatio = 1; // Default is square (1:1)

  @property({ type: Number })
  maxWidth = 500; // Default max width in pixels

  @property({ type: Number })
  maxHeight = 500; // Default max height in pixels

  @property({ type: String })
  acceptedFileTypes = 'image/*'; // Default accepts all image types

  @state()
  private pickerState: ImagePickerState = ImagePickerState.Selection;

  @state()
  private selectedImage: string | null = null;

  @state()
  private selectedImageBlob: string | null = null;

  @state()
  private croppedImage: string | null = null;

  @state()
  private errorMessage: string | null = null;

  @state()
  private isLoading = false;

  @state()
  private isDragging = false;

  @query('#image-picker-dialog')
  private dialog!: SlDialog;

  @query('#file-input')
  private fileInput!: HTMLInputElement;

  /**
   * Opens the image picker dialog
   */
  public open() {
    this.resetState();
    void this.dialog.show();
  }

  /**
   * Closes the dialog
   */
  public close() {
    // Reset all image-related properties before closing
    this.resetState();

    // Hide the dialog
    void this.dialog.hide();
  }

  /**
   * Resets the component state
   */
  private resetState() {
    this.pickerState = ImagePickerState.Selection;

    // Clean up blob URLs to prevent memory leaks
    if (this.selectedImageBlob) {
      this.revokeBlob(this.selectedImageBlob);
    }

    // Reset all image-related properties
    this.selectedImageBlob = null;
    this.selectedImage = null;
    this.croppedImage = null;
    this.errorMessage = null;
    this.isLoading = false;
    this.isDragging = false;

    // Reset file input
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  /**
   * Converts a data URL to a Blob URL
   */
  private dataURLToBlob(dataURL: string): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        // Convert base64 to raw binary data held in a string
        const byteString = atob(dataURL.split(',')[1]);

        // Separate out the mime component
        const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];

        // Write the bytes of the string to an ArrayBuffer
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const intArray = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
          intArray[i] = byteString.charCodeAt(i);
        }

        // Create a blob with the ArrayBuffer and the mime type
        const blob = new Blob([arrayBuffer], { type: mimeString });
        const blobUrl = URL.createObjectURL(blob);

        resolve(blobUrl);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  /**
   * Revokes a Blob URL to free memory
   */
  private revokeBlob(blobUrl: string | null) {
    if (blobUrl && blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl);
    }
  }

  /**
   * Clean up resources when component is disconnected
   */
  disconnectedCallback() {
    this.revokeBlob(this.selectedImageBlob);
    super.disconnectedCallback();
  }

  /**
   * Handles file selection
   */
  private handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    this.processFile(input.files[0]);
  }

  /**
   * Process the selected or dropped file
   */
  private processFile(file: File) {
    // Check file type
    if (!file.type.startsWith('image/')) {
      this.errorMessage = `Please select a valid image ${terms.storedFile.lower}.`;
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.errorMessage = `Image ${terms.storedFile.lower} size should be less than 5MB.`;
      return;
    }

    this.errorMessage = null;
    this.isLoading = true;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        // Store the original data URL
        this.selectedImage = e.target?.result as string;

        // Convert data URL to Blob URL for the cropper
        this.selectedImageBlob = await this.dataURLToBlob(this.selectedImage);

        // Generate an initial preview image right away
        await this.generateInitialPreview(this.selectedImage);

        this.pickerState = ImagePickerState.Cropping;
      } catch (error) {
        console.error('Error converting to blob:', error);
        this.errorMessage = 'Error preparing the image for cropping.';
      } finally {
        this.isLoading = false;
      }
    };

    reader.onerror = () => {
      this.errorMessage = `Error reading the image ${terms.storedFile.lower}.`;
      this.isLoading = false;
    };

    reader.readAsDataURL(file);
  }

  /**
   * Generates an initial preview image without waiting for crop event
   */
  private async generateInitialPreview(imageDataUrl: string) {
    try {
      // Convert the image for preview
      const convertedImage = await this.convertImage(imageDataUrl, 200, 200);
      this.croppedImage = convertedImage;
    } catch (error) {
      console.error('Error generating initial preview:', error);
      // Don't set error message here as it's not critical
    }
  }

  /**
   * Handles drag enter event
   */
  private handleDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  /**
   * Handles drag over event
   */
  private handleDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  /**
   * Handles drag leave event
   */
  private handleDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  /**
   * Handles drop event
   */
  private handleDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (!event.dataTransfer) {
      return;
    }

    const files = event.dataTransfer.files;
    if (files.length === 0) {
      return;
    }

    // Process the first file only
    this.processFile(files[0]);
  }

  /**
   * Handles the crop event from LitImageCropper
   */
  private async handleCropEvent(event: CustomEvent) {
    try {
      // The cropped image is provided in the event detail
      const croppedImage = event.detail;

      // Convert the image right after cropping
      this.isLoading = true;
      const convertedImage = await this.convertImage(croppedImage, 200, 200);

      // Store the converted version
      this.croppedImage = convertedImage;

      this.isLoading = false;
    } catch (error) {
      console.error('Error converting cropped image:', error);
      this.errorMessage = 'Error processing the cropped image.';
      this.isLoading = false;
    }
  }

  /**
   * Confirms the selection and emits the cropped image data
   */
  private async confirmSelection() {
    if (!this.croppedImage) {
      this.errorMessage = 'No image has been cropped.';
      return;
    }

    try {
      this.isLoading = true;

      // Create a local copy of the cropped image before we reset everything
      const imageToSend = this.croppedImage;
      const originalImage = this.selectedImage;

      // We can now use the already converted image directly
      this.dispatchEvent(
        new CustomEvent('image-selected', {
          detail: {
            imageBase64: imageToSend,
            originalImage: originalImage,
          },
          bubbles: true,
          composed: true,
        })
      );

      this.close();
    } catch (error) {
      console.error('Error processing image:', error);
      this.errorMessage = 'Error processing the image.';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Converts and resizes an image to the specified dimensions while maintaining aspect ratio
   * @param dataUrl The data URL of the image to convert
   * @param maxWidth The maximum width of the converted image
   * @param maxHeight The maximum height of the converted image
   * @returns A promise that resolves to the converted image as a data URL
   */
  private convertImage(
    dataUrl: string,
    maxWidth: number,
    maxHeight: number
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round(height * (maxWidth / width));
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round(width * (maxHeight / height));
            height = maxHeight;
          }
        }

        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Use high quality image resizing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw the image on the canvas with new dimensions
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to data URL with reasonable quality JPEG
        // 0.85 provides a good balance between quality and file size
        const convertedDataUrl = canvas.toDataURL('image/jpeg', 0.85);

        resolve(convertedDataUrl);
      };

      img.onerror = () => {
        reject(new Error('Error loading image for conversion'));
      };

      img.src = dataUrl;
    });
  }

  /**
   * Renders the file selection template
   */
  private getSelectionTemplate() {
    return html`
      <div class="image-picker-container selection">
        <div
          class="upload-area ${this.isDragging ? 'dragging' : ''}"
          @click=${() => this.fileInput.click()}
          @dragenter=${this.handleDragEnter}
          @dragover=${this.handleDragOver}
          @dragleave=${this.handleDragLeave}
          @drop=${this.handleDrop}
        >
          <sl-icon name="cloud-upload" class="upload-icon"></sl-icon>
          <p>Click to select an image or drag and drop here</p>
          <p class="file-types">Accepted formats: JPG, PNG, GIF, WebP</p>
        </div>

        <input
          type="file"
          id="file-input"
          accept="${this.acceptedFileTypes}"
          @change=${this.handleFileSelect}
          style="display: none;"
        />

        ${
          this.errorMessage
            ? html`<sl-alert variant="danger" open>
                ${this.errorMessage}
              </sl-alert>`
            : null
        }
      </div>

      <div slot="footer">
        <sl-button variant="neutral" @click=${this.close}> Cancel </sl-button>
      </div>
    `;
  }

  /**
   * Renders the image cropping template
   */
  private getCroppingTemplate() {
    return html`
      <div class="image-picker-container cropping">
        <p class="instructions">
          Drag the image to position it within the circle. Use the slider to
          zoom in or out.
        </p>

        <div class="cropping-layout">
          <div class="preview-avatar-container">
            ${
              this.croppedImage
                ? html`
                    <div class="preview-avatar">
                      <h3>Profile Preview</h3>
                      <div class="avatar-container">
                        <img
                          src="${this.croppedImage}"
                          alt="Avatar preview"
                          class="avatar-preview"
                        />
                      </div>
                    </div>

                    <div class="preview-avatar">
                      <h3>Nav Bar Size</h3>
                      <div class="avatar-container small">
                        <img
                          src="${this.croppedImage}"
                          alt="Small avatar preview"
                          class="avatar-preview"
                        />
                      </div>
                    </div>
                  `
                : html`
                    <div class="preview-avatar">
                      <h3>Profile Preview</h3>
                      <div class="avatar-container empty">
                        <sl-icon
                          name="person-circle"
                          class="avatar-placeholder"
                        ></sl-icon>
                      </div>
                    </div>

                    <div class="preview-avatar">
                      <h3>Nav Bar Size</h3>
                      <div class="avatar-container small empty">
                        <sl-icon
                          name="person-circle"
                          class="avatar-placeholder small"
                        ></sl-icon>
                      </div>
                    </div>
                  `
            }
          </div>

          <div class="cropper-container">
            ${
              this.selectedImageBlob
                ? html`
                    <lit-image-cropper
                      src="${this.selectedImageBlob}"
                      @on-image-cropped=${this.handleCropEvent}
                    ></lit-image-cropper>
                  `
                : html`<p>No image selected</p>`
            }
          </div>
        </div>

        ${
          this.errorMessage
            ? html`<sl-alert variant="danger" open>
                ${this.errorMessage}
              </sl-alert>`
            : null
        }
      </div>

      <div slot="footer">
        <sl-button
          variant="neutral"
          @click=${() => {
            this.pickerState = ImagePickerState.Selection;
          }}
        >
          Back
        </sl-button>
        <sl-button
          variant="primary"
          @click=${this.confirmSelection}
          ?disabled=${!this.croppedImage || this.isLoading}
        >
          ${this.isLoading ? html`<sl-spinner></sl-spinner>` : 'OK'}
        </sl-button>
      </div>
    `;
  }

  render() {
    let dialogTitle;
    let contentTemplate;

    switch (this.pickerState) {
      case ImagePickerState.Selection:
        dialogTitle = this.dialogTitle;
        contentTemplate = this.getSelectionTemplate();
        break;
      case ImagePickerState.Cropping:
        dialogTitle = 'Crop and Preview Image';
        contentTemplate = this.getCroppingTemplate();
        break;
    }

    return html`
      <sl-dialog
        id="image-picker-dialog"
        label="${dialogTitle}"
        style="--width: 50vw; max-width: 800px;"
        @sl-request-close=${(e: CustomEvent) => {
          if (e.detail.source === 'overlay') e.preventDefault();
        }}
      >
        ${
          this.isLoading && this.pickerState === ImagePickerState.Selection
            ? html`
                <div class="loading-overlay">
                  <sl-spinner></sl-spinner>
                  <p>Processing...</p>
                </div>
              `
            : null
        }
        ${contentTemplate}
      </sl-dialog>
    `;
  }
}
